/**
 * Per-Safe SRC-20 token list — explorer REST client (spec §9).
 *
 * Defensive decoder: missing optional fields don't throw; malformed records
 * are skipped. A different mainnet explorer JSON shape is handled by
 * `cfg.explorerListDecoder` on the registry entry.
 */
import type { Address } from 'viem'
import { isAddress } from 'viem'
import type { Src20ChainConfig } from '../registry'
import { buildTokenListUrl } from '../registry'
import type { ExplorerSrc20Token, Src20TokenInfo } from '../types'

/** Try common JSON shapes returned by SocialScan-like explorers. */
const extractList = (raw: unknown): unknown[] => {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    if (Array.isArray(obj.items)) return obj.items
    if (Array.isArray(obj.tokens)) return obj.tokens
    if (Array.isArray(obj.result)) return obj.result
    if (Array.isArray(obj.data)) return obj.data
  }
  return []
}

const decodeOne = (raw: unknown, cfg: Src20ChainConfig): Src20TokenInfo | undefined => {
  if (!raw || typeof raw !== 'object') return undefined
  const obj = raw as Partial<ExplorerSrc20Token> & Record<string, unknown>

  // Some explorers nest the token under `token` / `contract` keys.
  const tokenLike =
    typeof obj.token === 'object' && obj.token
      ? (obj.token as Record<string, unknown>)
      : typeof obj.contract === 'object' && obj.contract
        ? (obj.contract as Record<string, unknown>)
        : (obj as Record<string, unknown>)

  const addressRaw =
    (typeof tokenLike.address === 'string' && tokenLike.address) ||
    (typeof tokenLike.token_address === 'string' && tokenLike.token_address) ||
    (typeof tokenLike.contractAddress === 'string' && tokenLike.contractAddress) ||
    (typeof tokenLike.contract_address === 'string' && tokenLike.contract_address) ||
    ''
  // Some explorers return mixed-case addresses without strict EIP-55 checksums.
  // Don't fail-close on checksum — the address only needs to be syntactically valid.
  if (!isAddress(addressRaw, { strict: false })) return undefined

  const symbol =
    (typeof tokenLike.symbol === 'string' && tokenLike.symbol) ||
    (typeof tokenLike.token_symbol === 'string' && tokenLike.token_symbol) ||
    'SRC20'
  const name =
    (typeof tokenLike.name === 'string' && tokenLike.name) ||
    (typeof tokenLike.token_name === 'string' && tokenLike.token_name) ||
    symbol
  const decimalsRaw = tokenLike.decimals ?? tokenLike.token_decimals
  const decimals =
    typeof decimalsRaw === 'number'
      ? decimalsRaw
      : typeof decimalsRaw === 'string' && /^\d+$/.test(decimalsRaw)
        ? Number(decimalsRaw)
        : cfg.defaultDecimals

  const iconUrl =
    (typeof tokenLike.icon_url === 'string' && tokenLike.icon_url) ||
    (typeof tokenLike.iconUrl === 'string' && tokenLike.iconUrl) ||
    (typeof tokenLike.logoURI === 'string' && tokenLike.logoURI) ||
    (typeof tokenLike.token_icon_url === 'string' && tokenLike.token_icon_url) ||
    cfg.defaultTokenIconUrl

  return {
    address: addressRaw as Address,
    symbol,
    name,
    decimals,
    iconUrl,
    source: 'explorer',
  }
}

/** Decode a generic SocialScan-like JSON payload to `Src20TokenInfo[]`. */
export const decodeExplorerList = (raw: unknown, cfg: Src20ChainConfig): Src20TokenInfo[] => {
  if (cfg.explorerListDecoder) return cfg.explorerListDecoder(raw)
  const list = extractList(raw)
  const out: Src20TokenInfo[] = []
  for (const entry of list) {
    const decoded = decodeOne(entry, cfg)
    if (decoded) out.push(decoded)
  }
  return out
}

/** Read the SocialScan-style `total` field from a paginated response, if present. */
const extractTotal = (raw: unknown): number | undefined => {
  if (raw && typeof raw === 'object') {
    const total = (raw as Record<string, unknown>).total
    if (typeof total === 'number' && Number.isFinite(total)) return total
  }
  return undefined
}

/** Append/override `page` and `size` query params on a URL. */
const withPageParams = (baseUrl: string, page: number, size: number): string => {
  const [path, query = ''] = baseUrl.split('?')
  const params = new URLSearchParams(query)
  params.set('page', String(page))
  params.set('size', String(size))
  return `${path}?${params.toString()}`
}

const PAGE_SIZE = 100
const MAX_PAGES = 50 // hard cap (5000 tokens) — guards against a misbehaving explorer

const fetchPage = async (url: string, cfg: Src20ChainConfig): Promise<{ tokens: Src20TokenInfo[]; total?: number }> => {
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) {
    throw new Error(`Explorer returned ${res.status} for ${url}`)
  }
  const json: unknown = await res.json().catch(() => null)
  return { tokens: decodeExplorerList(json, cfg), total: extractTotal(json) }
}

/**
 * Fetch the FULL per-Safe SRC-20 token list from the explorer, walking
 * pages until every token is collected.
 *
 * The SocialScan endpoint paginates (`{ data, total, page, size }`); a single
 * request only returns the first ~50. We page with `?page=N&size=100` and stop
 * when we've collected `total` items, a page comes back empty/short, or the
 * `MAX_PAGES` safety cap is hit (which is logged upstream). De-duplicates by
 * address in case pages overlap.
 *
 * Errors propagate to the caller. An empty list is a valid response (`[]`).
 */
export const fetchExplorerTokenList = async (
  cfg: Src20ChainConfig,
  safeAddress: Address,
): Promise<Src20TokenInfo[]> => {
  const baseUrl = buildTokenListUrl(cfg, safeAddress)
  const byAddress = new Map<string, Src20TokenInfo>()
  let total: number | undefined

  for (let page = 1; page <= MAX_PAGES; page++) {
    const url = withPageParams(baseUrl, page, PAGE_SIZE)
    const { tokens, total: pageTotal } = await fetchPage(url, cfg)
    if (pageTotal !== undefined) total = pageTotal

    const sizeBefore = byAddress.size
    for (const t of tokens) {
      const key = t.address.toLowerCase()
      if (!byAddress.has(key)) byAddress.set(key, t)
    }

    // Empty page → nothing more to fetch.
    if (tokens.length === 0) break
    // No new addresses (explorer ignored `page` and returned the same set) →
    // avoid looping forever.
    if (byAddress.size === sizeBefore) break
    // Reached the explorer's reported total → done. This is the primary
    // stop signal and is robust even if the endpoint caps page size below
    // our request.
    if (total !== undefined && byAddress.size >= total) break
    // Total unknown and a short page came back → assume it was the last one.
    if (total === undefined && tokens.length < PAGE_SIZE) break
  }

  return [...byAddress.values()]
}
